import { Api, Message } from '../myApi';

export interface IChat {
  sessionId: string;
  author: string;
  group: string;
  timestamp: string;
  messages: IMessage[];
}

export interface IMessage {
  source: 'bot' | 'user' | 'system';
  name?: string;
  message: string;
  timestamp?: string;
}

export interface ICase {
  caseId: string;
  chats: IChat[];
  status: {
    status: string;
    hasUserChat: boolean;
    hasTechnicanChat: boolean;
  };
}

// Initialize the API client
const api = new Api({
  baseUrl: 'http://localhost:3000',
  baseApiParams: {
    secure: false, // Mock server doesn't use HTTPS
    headers: {
      'Content-Type': 'application/json',
    },
  },
});

/**
 * Fetches all cases from the mock server.
 * @returns {Promise<Case[]>} An array of cases.
 */
export async function fetchCases(): Promise<ICase[]> {
  try {
    const response = await fetch(`${api.baseUrl}/cases`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cases: ${response.statusText}`);
    }

    const data = await response.json();
    return Object.keys(data).map(caseId => ({
      caseId,
      ...data[caseId],
    }));
  } catch (error) {
    console.error('Error fetching cases:', error);
    throw error;
  }
}

/**
 * Fetches chats for a specific case ID.
 * @param {string} caseId The ID of the case.
 * @returns {Promise<IChat[]>} An array of chats for the case.
 */
export async function fetchChats(caseId: string): Promise<IChat[]> {
  try {
    const response = await api.cases.chatsDetail(caseId);
    if (!Array.isArray(response.data)) {
      throw new Error('Invalid response format');
    }

    // Ensure all required properties are present
    return response.data.map(chat => ({
      sessionId: chat.sessionId || `s-${Date.now()}`,
      author: chat.author || 'Unknown',
      group: chat.group || 'Unknown',
      timestamp: chat.timestamp || new Date().toISOString(),
      messages: chat.messages || [],
    })) as IChat[];
  } catch (error) {
    console.error(`Error fetching chats for case ${caseId}:`, error);
    throw error;
  }
}

/**
 * Adds a new chat to a specific case.
 * @param {string} caseId The ID of the case.
 * @param {string} author The author of the chat (e.g., 'Technican' or 'User').
 * @param {string} group The group of the chat (e.g., 'Technican' or 'User').
 * @returns {Promise<IChat>} The newly added chat.
 */
export async function addChat(caseId: string, author: string, group: string): Promise<IChat> {
  try {
    const newChat = {
      sessionId: `s-${Date.now()}`,
      author: author as 'Technican' | 'User',
      group: group as 'Technican' | 'User',
      timestamp: new Date().toISOString(),
    };

    const response = await api.cases.chatsCreate(caseId, newChat);
    return response.data as IChat;
  } catch (error) {
    console.error(`Error adding chat to case ${caseId}:`, error);
    throw error;
  }
}

/**
 * Deletes all chats for a specific case.
 * @param {string} caseId The ID of the case.
 * @returns {Promise<void>} A promise that resolves when all chats are deleted.
 */
export async function deleteAllChats(caseId: string): Promise<void> {
  try {
    await api.cases.chatsDelete(caseId);
  } catch (error) {
    console.error(`Error deleting all chats for case ${caseId}:`, error);
    throw error;
  }
}

/**
 * Fetches the status of a specific case.
 * @param {string} caseId The ID of the case.
 * @returns {Promise<{ status: string; hasUserChat: boolean; hasTechnicanChat: boolean }>} The status of the case.
 */
export async function getCaseStatus(caseId: string): Promise<{ status: string; hasUserChat: boolean; hasTechnicanChat: boolean }> {
  try {
    const response = await api.cases.statusDetail(caseId);
    return {
      status: response.data.status || 'unknown',
      hasUserChat: response.data.hasUserChat || false,
      hasTechnicanChat: response.data.hasTechnicanChat || false,
    };
  } catch (error) {
    console.error(`Error fetching status for case ${caseId}:`, error);
    throw error;
  }
}

/**
 * Fetches the status of a specific case and returns only the status as a string.
 * @param {string} caseId - The ID of the case.
 * @returns {Promise<string>} A promise that resolves to the status of the case.
 * If an error occurs, it returns 'unknown'.
 */
export async function getCaseStatusString(caseId: string): Promise<string> {
  try {
    const { status } = await getCaseStatus(caseId);
    console.log(`[getCaseStatusString] Fetched status:`, status);
    return status;
  } catch (error) {
    console.error(`Error fetching case status for ${caseId}:`, error);
    return 'unknown';
  }
}

/**
 * Sends a message to a specific chat session.
 * @param {string} caseId The ID of the case.
 * @param {string} sessionId The ID of the chat session.
 * @param {IMessage} message The message to be sent.
 * @returns {Promise<IMessage>} The new message with a timestamp.
 */
export async function sendMessage(caseId: string, sessionId: string, message: IMessage): Promise<IMessage> {
  try {
    const response = await api.cases.sendMessageCreate(caseId, sessionId, message as Message);
    return response.data as IMessage;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
}
